import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, Clock, LogIn } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PRODUCTS, type Product } from '@/lib/products'
import { contactPath } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { ProductIconTile } from '@/components/shared/ProductBits'

/**
 * "Sign in" app launcher. Existing customers come to tdrem.com mostly to
 * reach their app, so this is one click from every page.
 */
export default function AppLauncher({ className }: { className?: string }) {
  const { t } = useTranslation('common')

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex h-10 items-center gap-2 rounded-xl border bg-card px-3.5 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent',
          className
        )}
      >
        <LogIn className="h-4 w-4 flip-rtl" />
        {t('actions.signIn')}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-[min(20rem,calc(100vw-2rem))] rounded-2xl p-2">
        <div className="px-2 pb-2 pt-1">
          <p className="text-sm font-semibold">{t('launcher.title')}</p>
          <p className="text-xs text-muted-foreground">{t('launcher.subtitle')}</p>
        </div>
        {PRODUCTS.map((p) => (
          <DropdownMenuItem key={p.id} asChild className="cursor-pointer rounded-xl p-2">
            <AppLauncherLink product={p} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** A single row: icon · name · host → opens app (or waitlist). Also used in the mobile drawer. */
export const AppLauncherLink = forwardRef<
  HTMLAnchorElement,
  { product: Product; onNavigate?: () => void } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
>(function AppLauncherLink({ product, onNavigate, className, onClick, ...rest }, ref) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    onNavigate?.()
  }
  const { t } = useTranslation(['products', 'common'])
  const soon = product.status === 'comingSoon'
  const inner = (
    <>
      <ProductIconTile product={product} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{t(`${product.id}.name`)}</span>
        <span className="block truncate text-xs text-muted-foreground" dir="ltr">
          {soon ? t('common:launcher.soon') : product.appHost}
        </span>
      </span>
      {soon ? (
        <Clock className="h-4 w-4 text-muted-foreground" />
      ) : (
        <ArrowUpRight className="h-4 w-4 text-muted-foreground flip-rtl" />
      )}
    </>
  )
  const cls = cn('flex w-full items-center gap-3', className)

  return soon ? (
    <Link
      {...rest}
      ref={ref}
      to={contactPath({ product: product.id, topic: 'waitlist' })}
      className={cls}
      onClick={handleClick}
    >
      {inner}
    </Link>
  ) : (
    <a
      {...rest}
      ref={ref}
      href={product.appUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cls}
      onClick={handleClick}
    >
      {inner}
    </a>
  )
})
